import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ViewType from "~/@types/viewType";
import { ItemSummaryDocument } from "~/gql/graphqlBase.generated";
import { CustomerLocationFieldsFragment } from "~/gql/queries/customer.generated";
import { ReplenishmentRuleSummaryFragment } from "~/gql/queries/restock/replenishmentRules.generated";

export interface SelectedRuleWithDetails
	extends Omit<ReplenishmentRuleSummaryFragment, "linkedItemIds" | "linkedLocationIds"> {
	linkedItemIds?: string[];
	linkedLocationIds?: string[];
	quantityFloor?: number;
}

interface RulesState {
	cachedItems: ItemSummaryDocument[];
	cachedLocations: CustomerLocationFieldsFragment[];
	deleteRuleId: string | null;
	ruleFormEditing: boolean;
	ruleFormOpen: boolean;
	searchTerm: string;
	selectedRule?: SelectedRuleWithDetails;
	upsertRuleId: string | null;
	viewType: ViewType;
}

const initialState: RulesState = {
	cachedItems: [],
	cachedLocations: [],
	deleteRuleId: null,
	ruleFormEditing: false,
	ruleFormOpen: false,
	searchTerm: "",
	upsertRuleId: null,
	viewType: "grid"
};

const rulesSlice = createSlice({
	initialState,
	name: "restockRules",
	reducers: {
		clearDeleteRuleId: (state) => {
			state.deleteRuleId = null;
		},
		clearSelectedRule: (state) => {
			state.selectedRule = undefined;
		},
		clearUpsertRuleId: (state) => {
			state.upsertRuleId = null;
		},
		closeRuleForm: (state) => {
			state.ruleFormEditing = false;
			state.ruleFormOpen = false;
		},
		openRuleForm: (state) => {
			state.ruleFormOpen = true;
		},
		openRuleFormForEdit: (state) => {
			state.ruleFormEditing = true;
			state.ruleFormOpen = true;
		},
		setCachedItems: (state, action: PayloadAction<ItemSummaryDocument[]>) => {
			state.cachedItems = action.payload;
		},
		setCachedLocations: (state, action: PayloadAction<CustomerLocationFieldsFragment[]>) => {
			state.cachedLocations = action.payload;
		},
		setDeleteRuleId: (state, action: PayloadAction<string | null>) => {
			state.deleteRuleId = action.payload;
		},
		setSearchTerm: (state, action: PayloadAction<string>) => {
			state.searchTerm = action.payload;
		},
		setSelectedRule: (state, action: PayloadAction<SelectedRuleWithDetails | undefined>) => {
			state.selectedRule = action.payload;
		},
		setUpsertRuleId: (state, action: PayloadAction<string | null>) => {
			state.upsertRuleId = action.payload;
		},
		setViewType: (state, action: PayloadAction<ViewType>) => {
			state.viewType = action.payload;
		}
	}
});

export const {
	clearDeleteRuleId,
	clearSelectedRule,
	clearUpsertRuleId,
	closeRuleForm,
	openRuleForm,
	openRuleFormForEdit,
	setCachedItems,
	setCachedLocations,
	setDeleteRuleId,
	setSearchTerm,
	setSelectedRule,
	setUpsertRuleId,
	setViewType
} = rulesSlice.actions;

export default rulesSlice.reducer;
